import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FURD_TECH_LOGO = '/furd-tech-logo.png';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send reset email');
      }
    } catch (err) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row" data-testid="forgot-password-success">
        {/* Mobile Hero */}
        <div className="lg:hidden bg-gradient-to-b from-[#0a1628] via-[#0d1f35] to-[#1e3a5f] relative overflow-hidden px-6 pt-12 pb-8">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-80 bg-emerald-500/15 blur-[100px] rounded-full" />
          <div className="absolute top-10 left-1/2 -translate-x-1/2 w-56 h-56 bg-emerald-400/20 blur-[60px] rounded-full" />
          
          <div className="relative z-10 text-center">
            <div className="relative mx-auto mb-4 w-fit">
              <div className="absolute inset-0 bg-emerald-400/30 blur-3xl rounded-full scale-[2]" />
              <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-16 w-auto mx-auto relative z-10 drop-shadow-2xl" />
            </div>
            <p className="text-white/70 text-base font-light">MEP Consultancy OS</p>
          </div>
        </div>

        {/* Mobile Content */}
        <div className="lg:hidden flex-1 bg-white px-6 py-8 -mt-4 rounded-t-3xl relative z-20 shadow-2xl">
          <div className="max-w-sm mx-auto text-center">
            <div className="mx-auto w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
              <CheckCircle className="w-7 h-7 text-emerald-600" />
            </div>
            <h1 className="font-heading text-xl font-bold text-[#1e3a5f] mb-2">Check your email</h1>
            <p className="text-slate-500 text-sm mb-5">
              If an account exists for <strong className="text-slate-700">{email}</strong>, we&apos;ve sent a reset link.
            </p>
            
            <div className="bg-slate-50 rounded-xl p-4 text-left mb-5">
              <p className="text-sm text-slate-700 font-medium mb-2">Didn&apos;t receive it?</p>
              <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
                <li>Check your spam folder</li>
                <li>Verify the email address</li>
                <li>Wait a few minutes</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <Button variant="outline" onClick={() => setSubmitted(false)} className="w-full rounded-xl h-11">
                Try a different email
              </Button>
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full text-[#1e3a5f]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to login
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Desktop Content */}
        <div className="hidden lg:flex flex-1 items-center justify-center p-16 bg-white">
          <div className="w-full max-w-md text-center">
            <div className="flex items-center gap-3 justify-center mb-12">
              <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-10 w-auto" />
              <span className="font-heading text-2xl font-bold text-[#1e3a5f]">OpsFlow</span>
            </div>
            
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-[#1e3a5f] mb-3">Check your email</h1>
            <p className="text-slate-500 text-lg mb-8">
              If an account exists for <strong className="text-slate-700">{email}</strong>, we&apos;ve sent a password reset link.
            </p>
            
            <div className="bg-slate-50 rounded-xl p-6 text-left mb-8">
              <p className="font-medium text-slate-900 mb-3">Didn&apos;t receive the email?</p>
              <ul className="list-disc list-inside text-sm text-slate-600 space-y-1">
                <li>Check your spam or junk folder</li>
                <li>Make sure you entered the correct email</li>
                <li>Wait a few minutes and try again</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <Button variant="outline" onClick={() => setSubmitted(false)} className="w-full h-12 rounded-xl">
                Try a different email
              </Button>
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full text-[#1e3a5f]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to login
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Desktop Hero */}
        <div className="hidden lg:flex flex-1 bg-[#000000] items-center justify-center p-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-transparent to-slate-900/30" />
          <div className="relative z-10 text-center max-w-lg">
            <div className="relative mx-auto mb-8">
              <div className="absolute inset-0 bg-emerald-500/20 blur-3xl rounded-full scale-150 animate-pulse" />
              <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-24 w-auto mx-auto relative z-10" />
            </div>
            <p className="text-white/60 text-xl font-light">MEP Consultancy OS</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="forgot-password-page">
      {/* Mobile Hero */}
      <div className="lg:hidden bg-gradient-to-b from-[#0a1628] via-[#0d1f35] to-[#1e3a5f] relative overflow-hidden px-6 pt-12 pb-8">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-80 bg-emerald-500/15 blur-[100px] rounded-full" />
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-56 h-56 bg-emerald-400/20 blur-[60px] rounded-full animate-pulse" />
        
        <div className="relative z-10 text-center">
          <div className="relative mx-auto mb-4 w-fit">
            <div className="absolute inset-0 bg-emerald-400/30 blur-3xl rounded-full scale-[2]" />
            <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-16 w-auto mx-auto relative z-10 drop-shadow-2xl" />
          </div>
          <p className="text-white/70 text-base font-light">MEP Consultancy OS</p>
        </div>
      </div>

      {/* Mobile Form */}
      <div className="lg:hidden flex-1 bg-white px-6 py-8 -mt-4 rounded-t-3xl relative z-20 shadow-2xl">
        <div className="max-w-sm mx-auto">
          <div className="mx-auto w-12 h-12 bg-[#1e3a5f] rounded-xl flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-xl font-bold text-[#1e3a5f] mb-2 text-center">Forgot password?</h1>
          <p className="text-slate-500 text-sm mb-5 text-center">
            Enter your email and we&apos;ll send a reset link.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="email-mobile" className="text-slate-700 font-medium text-sm">Email address</Label>
              <Input
                id="email-mobile"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="h-11 rounded-xl bg-slate-50 border-slate-200 text-base"
                data-testid="forgot-password-email"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 bg-[#1e3a5f] hover:bg-[#162c46] rounded-xl font-semibold shadow-lg shadow-[#1e3a5f]/20"
              disabled={loading}
              data-testid="forgot-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send reset link'
              )}
            </Button>

            <div className="text-center pt-2">
              <Link to="/login" className="text-sm text-[#1e3a5f] font-medium">
                <ArrowLeft className="w-4 h-4 inline mr-1" />
                Back to login
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Desktop Form */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-16 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-10 w-auto" />
            <span className="font-heading text-2xl font-bold text-[#1e3a5f]">OpsFlow</span>
          </div>
          
          <div className="mx-auto w-14 h-14 bg-[#1e3a5f] rounded-xl flex items-center justify-center mb-6">
            <Mail className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-[#1e3a5f] mb-3">Forgot your password?</h1>
          <p className="text-slate-500 text-lg mb-8">
            Enter your email address and we&apos;ll send you a link to reset your password.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="h-12 rounded-xl bg-slate-50 border-slate-200"
                data-testid="forgot-password-email-desktop"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-[#1e3a5f] hover:bg-[#162c46] rounded-xl font-semibold"
              disabled={loading}
              data-testid="forgot-password-submit-desktop"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send reset link'
              )}
            </Button>

            <div className="text-center pt-2">
              <Link to="/login" className="text-sm text-slate-500 hover:text-[#1e3a5f]">
                <ArrowLeft className="w-4 h-4 inline mr-1" />
                Back to login
              </Link>
            </div>
          </form>
        </div>
      </div>
      
      {/* Desktop Hero */}
      <div className="hidden lg:flex flex-1 bg-[#000000] items-center justify-center p-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-transparent to-slate-900/30" />
        <div className="relative z-10 text-center max-w-lg">
          <div className="relative mx-auto mb-8">
            <div className="absolute inset-0 bg-emerald-500/20 blur-3xl rounded-full scale-150 animate-pulse" />
            <div className="absolute inset-0 bg-emerald-400/10 blur-2xl rounded-full scale-125" />
            <img src={FURD_TECH_LOGO} alt="FURD Tech" className="h-24 md:h-32 w-auto mx-auto relative z-10" />
          </div>
          <p className="text-white/60 text-xl font-light">MEP Consultancy OS</p>
        </div>
      </div>
    </div>
  );
}
